import { createFishingTitlesApi } from "../js/fishydex/fishing-titles-api.js";
import { createMissingTitlePredicate } from "../js/fishydex/title-progress.js";

export const TITLE_QUERY_CHANGED = "fishymap-title-query-changed";

// Query lifetime owns one lazy title request and one typed Current subscription.
// Catalog facts never enter Datastar or the map's durable preset payload.
export function createMapTitleQueryOwner({ repository, resolveApiUrl, readFishCatalog, readLanguage,
  guideCountLookup, onChange, api = createFishingTitlesApi({ resolveApiUrl }) }) {
  let current = repository.state();
  let catalog = null, request = null, language = null;
  let kind = "idle", active = false, disposed = false;
  let projection = { kind: "idle" };
  function publish() {
    if (disposed) return;
    const fish = readFishCatalog();
    if (kind !== "ready" || current.kind !== "ready" || !Array.isArray(fish)) {
      projection = { kind: kind === "ready" ? "unavailable" : kind };
    } else {
      const countForKey = guideCountLookup(current.fishGuideProgress);
      const missing = createMissingTitlePredicate(catalog.titles, current,
        entry => countForKey(entry.encyclopediaKey));
      projection = { kind: "ready", titles: catalog.titles,
        titleItems: catalog.titles.filter(title => title.itemId !== null)
          .map(title => ({ titleId: title.titleId, itemId: title.itemId })),
        missingTitleItemIds: [...new Set(fish.filter(missing).map(entry => entry.itemId))].sort((a,b) => a-b) };
    }
    onChange(projection);
  }
  async function ensure() {
    if (disposed) return;
    active = true;
    const nextLanguage = readLanguage();
    if (language === nextLanguage && kind !== "idle") return;
    language = nextLanguage;
    request?.abort();
    const controller = new AbortController();
    request = controller;
    catalog = null;
    kind = "loading";
    publish();
    try {
      const result = await api.catalog({ lang: language, signal: controller.signal });
      if (disposed || request !== controller) return;
      catalog = result;
      kind = "ready";
    } catch (error) {
      if (disposed || controller.signal.aborted || request !== controller) return;
      kind = "error";
    }
    publish();
  }
  const unsubscribe = repository.subscribe(next => { current = next; if (active) publish(); });
  return {
    ensure,
    refresh() { if (active) { if (language !== readLanguage()) void ensure(); else publish(); } },
    get projection() { return projection; },
    dispose() { disposed = true; request?.abort(); unsubscribe(); },
  };
}
